import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
  StatusBar,
  RefreshControl,
  Modal,
  TextInput,
  Alert,
  Animated,
  ActivityIndicator,
  FlatList,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width, height } = Dimensions.get('window');

// JobTemplates Component
export default function JobTemplates({ navigation, route }) {
  // Core state management
  const [isLoading, setIsLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [customizedTemplate, setCustomizedTemplate] = useState(null);
  
  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Template categories
  const categories = [
    { id: 'all', name: 'All Templates', icon: 'apps', count: 24 },
    { id: 'engineering', name: 'Engineering', icon: 'cog', count: 8 },
    { id: 'construction', name: 'Construction', icon: 'hammer', count: 6 },
    { id: 'management', name: 'Management', icon: 'account-tie', count: 4 },
    { id: 'design', name: 'Design', icon: 'palette', count: 3 },
    { id: 'sales', name: 'Sales', icon: 'chart-line', count: 3 },
  ];

  // Comprehensive job templates data
  const [templatesData, setTemplatesData] = useState({
    engineering: [
      {
        id: 'eng_001',
        title: 'Senior Software Engineer',
        category: 'engineering',
        industry: 'Technology',
        level: 'Senior',
        type: 'Full-time',
        popularity: 95,
        usage: 1247,
        rating: 4.9,
        tags: ['React', 'Node.js', 'TypeScript', 'AWS'],
        description: 'Join our dynamic engineering team to build cutting-edge software solutions.',
        requirements: [
          '5+ years of software development experience',
          'Proficiency in React, Node.js, and TypeScript',
          'Experience with cloud platforms (AWS/Azure)',
          'Strong problem-solving and communication skills',
          'Bachelor\'s degree in Computer Science or related field'
        ],
        responsibilities: [
          'Design and develop scalable web applications',
          'Collaborate with cross-functional teams',
          'Mentor junior developers',
          'Participate in code reviews and technical discussions',
          'Contribute to architectural decisions'
        ],
        benefits: [
          'Competitive salary: £70,000 - £90,000',
          'Flexible working arrangements',
          'Professional development budget',
          'Health and dental insurance',
          'Stock options'
        ],
        companyInfo: {
          name: '[Company Name]',
          location: '[Location]',
          size: '[Company Size]',
          industry: '[Industry]'
        },
        customFields: {
          salaryRange: { min: 70000, max: 90000, currency: 'GBP' },
          location: 'London, UK',
          remote: true,
          experienceYears: { min: 5, max: 10 },
          educationLevel: 'Bachelor\'s Degree',
          skills: ['React', 'Node.js', 'TypeScript', 'AWS', 'Docker', 'Kubernetes']
        }
      },
      {
        id: 'eng_002',
        title: 'DevOps Engineer',
        category: 'engineering',
        industry: 'Technology',
        level: 'Mid-Senior',
        type: 'Full-time',
        popularity: 88,
        usage: 892,
        rating: 4.7,
        tags: ['AWS', 'Docker', 'Kubernetes', 'CI/CD'],
        description: 'Lead our infrastructure automation and deployment processes.',
        requirements: [
          '3+ years of DevOps/Infrastructure experience',
          'Expertise in AWS/Azure cloud platforms',
          'Proficiency with Docker and Kubernetes',
          'Experience with CI/CD pipelines',
          'Strong scripting skills (Python, Bash)'
        ],
        responsibilities: [
          'Design and maintain cloud infrastructure',
          'Implement CI/CD pipelines',
          'Monitor system performance and reliability',
          'Automate deployment processes',
          'Ensure security and compliance'
        ],
        benefits: [
          'Competitive salary: £60,000 - £80,000',
          'Remote work options',
          'Training and certification support',
          'Health benefits',
          'Performance bonuses'
        ],
        companyInfo: {
          name: '[Company Name]',
          location: '[Location]',
          size: '[Company Size]',
          industry: '[Industry]'
        },
        customFields: {
          salaryRange: { min: 60000, max: 80000, currency: 'GBP' },
          location: 'Manchester, UK',
          remote: true,
          experienceYears: { min: 3, max: 8 },
          educationLevel: 'Bachelor\'s Degree',
          skills: ['AWS', 'Docker', 'Kubernetes', 'Jenkins', 'Terraform', 'Python']
        }
      },
      {
        id: 'eng_003',
        title: 'Frontend Developer',
        category: 'engineering',
        industry: 'Technology',
        level: 'Mid-Level',
        type: 'Full-time',
        popularity: 92,
        usage: 1156,
        rating: 4.8,
        tags: ['React', 'Vue.js', 'JavaScript', 'CSS'],
        description: 'Create beautiful and responsive user interfaces for our web applications.',
        requirements: [
          '3+ years of frontend development experience',
          'Proficiency in React or Vue.js',
          'Strong JavaScript, HTML, and CSS skills',
          'Experience with responsive design',
          'Knowledge of modern build tools'
        ],
        responsibilities: [
          'Develop responsive web applications',
          'Collaborate with UX/UI designers',
          'Optimize applications for performance',
          'Write clean, maintainable code',
          'Participate in agile development processes'
        ],
        benefits: [
          'Competitive salary: £45,000 - £65,000',
          'Flexible working hours',
          'Learning and development opportunities',
          'Health insurance',
          'Team building activities'
        ],
        companyInfo: {
          name: '[Company Name]',
          location: '[Location]',
          size: '[Company Size]',
          industry: '[Industry]'
        },
        customFields: {
          salaryRange: { min: 45000, max: 65000, currency: 'GBP' },
          location: 'Birmingham, UK',
          remote: false,
          experienceYears: { min: 3, max: 6 },
          educationLevel: 'Bachelor\'s Degree',
          skills: ['React', 'Vue.js', 'JavaScript', 'CSS', 'HTML', 'Webpack']
        }
      }
    ],
    construction: [
      {
        id: 'con_001',
        title: 'Site Manager',
        category: 'construction',
        industry: 'Construction',
        level: 'Senior',
        type: 'Full-time',
        popularity: 85,
        usage: 634,
        rating: 4.6,
        tags: ['Project Management', 'Health & Safety', 'CSCS', 'Leadership'],
        description: 'Lead construction projects from start to finish ensuring quality and safety.',
        requirements: [
          '5+ years of construction site management experience',
          'CSCS card and relevant certifications',
          'Strong knowledge of health and safety regulations',
          'Excellent leadership and communication skills',
          'Experience with construction project management'
        ],
        responsibilities: [
          'Oversee daily construction operations',
          'Ensure compliance with health and safety standards',
          'Coordinate with subcontractors and suppliers',
          'Monitor project progress and quality',
          'Manage site personnel and resources'
        ],
        benefits: [
          'Competitive salary: £50,000 - £70,000',
          'Company vehicle',
          'Health and safety training',
          'Pension scheme',
          'Career progression opportunities'
        ],
        companyInfo: {
          name: '[Company Name]',
          location: '[Location]',
          size: '[Company Size]',
          industry: '[Industry]'
        },
        customFields: {
          salaryRange: { min: 50000, max: 70000, currency: 'GBP' },
          location: 'Various Sites, UK',
          remote: false,
          experienceYears: { min: 5, max: 15 },
          educationLevel: 'HNC/HND or equivalent',
          skills: ['Project Management', 'Health & Safety', 'CSCS', 'Leadership', 'Planning']
        }
      },
      {
        id: 'con_002',
        title: 'Quantity Surveyor',
        category: 'construction',
        industry: 'Construction',
        level: 'Mid-Senior',
        type: 'Full-time',
        popularity: 78,
        usage: 445,
        rating: 4.5,
        tags: ['Cost Management', 'RICS', 'Contracts', 'Estimation'],
        description: 'Manage project costs and ensure financial efficiency in construction projects.',
        requirements: [
          '3+ years of quantity surveying experience',
          'RICS qualification or working towards',
          'Strong knowledge of construction contracts',
          'Excellent analytical and numerical skills',
          'Experience with cost estimation software'
        ],
        responsibilities: [
          'Prepare cost estimates and budgets',
          'Monitor project expenditure',
          'Negotiate with suppliers and contractors',
          'Prepare tender documents',
          'Conduct cost analysis and reporting'
        ],
        benefits: [
          'Competitive salary: £40,000 - £60,000',
          'Professional development support',
          'Flexible working arrangements',
          'Health benefits',
          'Performance bonuses'
        ],
        companyInfo: {
          name: '[Company Name]',
          location: '[Location]',
          size: '[Company Size]',
          industry: '[Industry]'
        },
        customFields: {
          salaryRange: { min: 40000, max: 60000, currency: 'GBP' },
          location: 'Leeds, UK',
          remote: false,
          experienceYears: { min: 3, max: 8 },
          educationLevel: 'Bachelor\'s Degree',
          skills: ['Cost Management', 'RICS', 'Contracts', 'Estimation', 'Excel', 'AutoCAD']
        }
      }
    ],
    management: [
      {
        id: 'mgmt_001',
        title: 'Project Manager',
        category: 'management',
        industry: 'Various',
        level: 'Senior',
        type: 'Full-time',
        popularity: 90,
        usage: 987,
        rating: 4.7,
        tags: ['PMP', 'Agile', 'Scrum', 'Leadership'],
        description: 'Lead cross-functional teams to deliver projects on time and within budget.',
        requirements: [
          '5+ years of project management experience',
          'PMP or equivalent certification preferred',
          'Experience with Agile/Scrum methodologies',
          'Strong leadership and communication skills',
          'Bachelor\'s degree in relevant field'
        ],
        responsibilities: [
          'Plan and execute project deliverables',
          'Manage project timelines and budgets',
          'Lead cross-functional project teams',
          'Communicate with stakeholders',
          'Identify and mitigate project risks'
        ],
        benefits: [
          'Competitive salary: £55,000 - £75,000',
          'Flexible working arrangements',
          'Professional certification support',
          'Health and dental insurance',
          'Annual bonus scheme'
        ],
        companyInfo: {
          name: '[Company Name]',
          location: '[Location]',
          size: '[Company Size]',
          industry: '[Industry]'
        },
        customFields: {
          salaryRange: { min: 55000, max: 75000, currency: 'GBP' },
          location: 'London, UK',
          remote: true,
          experienceYears: { min: 5, max: 12 },
          educationLevel: 'Bachelor\'s Degree',
          skills: ['PMP', 'Agile', 'Scrum', 'Leadership', 'Risk Management', 'Stakeholder Management']
        }
      }
    ],
    design: [
      {
        id: 'des_001',
        title: 'UX/UI Designer',
        category: 'design',
        industry: 'Technology',
        level: 'Mid-Level',
        type: 'Full-time',
        popularity: 93,
        usage: 1089,
        rating: 4.9,
        tags: ['Figma', 'Adobe Creative Suite', 'User Research', 'Prototyping'],
        description: 'Design intuitive and engaging user experiences for our digital products.',
        requirements: [
          '3+ years of UX/UI design experience',
          'Proficiency in Figma and Adobe Creative Suite',
          'Strong portfolio demonstrating design skills',
          'Experience with user research and testing',
          'Knowledge of design systems and accessibility'
        ],
        responsibilities: [
          'Create wireframes, prototypes, and high-fidelity designs',
          'Conduct user research and usability testing',
          'Collaborate with product and engineering teams',
          'Maintain and evolve design systems',
          'Present design concepts to stakeholders'
        ],
        benefits: [
          'Competitive salary: £40,000 - £60,000',
          'Creative freedom and autonomy',
          'Latest design tools and equipment',
          'Conference and training budget',
          'Flexible working hours'
        ],
        companyInfo: {
          name: '[Company Name]',
          location: '[Location]',
          size: '[Company Size]',
          industry: '[Industry]'
        },
        customFields: {
          salaryRange: { min: 40000, max: 60000, currency: 'GBP' },
          location: 'Bristol, UK',
          remote: true,
          experienceYears: { min: 3, max: 7 },
          educationLevel: 'Bachelor\'s Degree',
          skills: ['Figma', 'Adobe Creative Suite', 'User Research', 'Prototyping', 'Design Systems']
        }
      }
    ],
    sales: [
      {
        id: 'sal_001',
        title: 'Sales Manager',
        category: 'sales',
        industry: 'Various',
        level: 'Senior',
        type: 'Full-time',
        popularity: 87,
        usage: 756,
        rating: 4.6,
        tags: ['B2B Sales', 'CRM', 'Team Leadership', 'Target Achievement'],
        description: 'Drive sales growth and lead a high-performing sales team.',
        requirements: [
          '5+ years of B2B sales experience',
          'Proven track record of meeting sales targets',
          'Experience managing sales teams',
          'Strong negotiation and communication skills',
          'CRM software proficiency'
        ],
        responsibilities: [
          'Develop and execute sales strategies',
          'Manage and mentor sales team',
          'Build relationships with key clients',
          'Monitor sales performance and KPIs',
          'Collaborate with marketing and product teams'
        ],
        benefits: [
          'Base salary: £45,000 + commission',
          'Uncapped commission structure',
          'Company car or car allowance',
          'Health and dental insurance',
          'Sales incentive trips'
        ],
        companyInfo: {
          name: '[Company Name]',
          location: '[Location]',
          size: '[Company Size]',
          industry: '[Industry]'
        },
        customFields: {
          salaryRange: { min: 45000, max: 80000, currency: 'GBP' },
          location: 'Edinburgh, UK',
          remote: false,
          experienceYears: { min: 5, max: 12 },
          educationLevel: 'Bachelor\'s Degree',
          skills: ['B2B Sales', 'CRM', 'Team Leadership', 'Target Achievement', 'Negotiation']
        }
      }
    ]
  });

  // Get all templates for display — built-in (templatesData) + custom from DB.
  // Custom templates are placed under their `category` so they appear in the
  // matching category tab AND in the "All" tab.
  const getAllTemplates = () => {
    const allTemplates = [];
    Object.values(templatesData).forEach(categoryTemplates => {
      allTemplates.push(...categoryTemplates);
    });
    allTemplates.push(...customTemplates);
    return allTemplates;
  };

  // Filter templates based on category and search
  const getFilteredTemplates = () => {
    let templates;
    if (selectedCategory === 'all') {
      templates = getAllTemplates();
    } else {
      const builtIn = templatesData[selectedCategory] || [];
      const custom  = customTemplates.filter(t => t.category === selectedCategory);
      templates = [...builtIn, ...custom];
    }
    
    if (searchQuery.trim()) {
      templates = templates.filter(template => 
        String(template.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
        String(template.industry ?? '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return templates;
  };

  // Custom templates pulled from /api/data/job_templates. We keep them in
  // a separate state slice so the built-in seed data stays static and the
  // user's own work is round-tripped to the DB. Merging happens at read
  // time in getAllTemplates() / getFilteredTemplates().
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loadingCustom, setLoadingCustom]     = useState(false);

  const API_BASE_URL_JT = 'https://api.jobeng.ai';
  const API_KEY_JT      = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
  const jtFetch = async (endpoint, opts = {}) => {
    const auth = (await AsyncStorage.getItem('auth_token'))
              || (await AsyncStorage.getItem('userToken'))
              || (await AsyncStorage.getItem('token'));
    const res = await fetch(`${API_BASE_URL_JT}${endpoint}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key':    API_KEY_JT,
        ...(auth ? { Authorization: `Bearer ${auth}` } : {}),
        ...(opts.headers || {}),
      },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  // ── Map DB row → the shape the rest of JobTemplates expects ──
  const dbRowToTemplate = (row) => ({
    id:           `db_${row.id}`,
    _dbId:        row.id,                   // original numeric id for edit/delete
    title:        row.title || 'Untitled',
    category:     row.category || 'other',
    industry:     row.industry || '',
    level:        row.level || '',
    type:         row.job_type || 'Full-time',
    popularity:   row.popularity || 0,
    usage:        row.usage_count || 0,
    rating:       Number(row.rating) || 0,
    tags:         Array.isArray(row.tags) ? row.tags : [],
    description:  row.description || '',
    requirements:     Array.isArray(row.requirements)     ? row.requirements     : [],
    responsibilities: Array.isArray(row.responsibilities) ? row.responsibilities : [],
    benefits:         Array.isArray(row.benefits)         ? row.benefits         : [],
    companyInfo: {
      name:     '[Company Name]', location: '[Location]',
      size:     '[Company Size]', industry: row.industry || '[Industry]',
    },
    customFields: {
      salaryRange: { min: Number(row.salary_min) || 0, max: Number(row.salary_max) || 0, currency: row.salary_currency || 'GBP' },
      location:    row.location || '',
      remote:      !!row.is_remote,
      experienceYears: { min: row.experience_min_years || 0, max: row.experience_max_years || 0 },
      educationLevel:  row.education_level || '',
      skills:          Array.isArray(row.skills) ? row.skills : [],
    },
    isCustom: true,
    status:   row.status || 'published',
  });

  const loadCustomTemplates = async () => {
    try {
      setLoadingCustom(true);
      const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10) || 0;
      if (!cid) { setCustomTemplates([]); return; }
      // Pull all templates owned by this company, plus any public ones.
      // We filter out drafts in the displayed list (user can still see them
      // in their own management view). For now we show only published.
      const r = await jtFetch(`/api/data/job_templates?company_id=${cid}&limit=200`);
      const rows = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
      const published = rows.filter(r2 => (r2.status || 'published') === 'published');
      const mapped = published.map(dbRowToTemplate);
      console.log('[JobTemplates] loaded custom templates:', mapped.length);
      setCustomTemplates(mapped);
    } catch (e) {
      console.warn('[JobTemplates] loadCustomTemplates:', e.message);
    } finally {
      setLoadingCustom(false);
    }
  };

  useEffect(() => {
    // Animate component entrance
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
    loadCompanyProfile();
    loadCustomTemplates();
  }, []);

  // Re-fetch custom templates whenever this screen regains focus, so a
  // template the user just created in CreateCustomTemplateScreen appears
  // here immediately on back-navigation.
  useEffect(() => {
    const unsub = navigation?.addListener?.('focus', () => {
      loadCustomTemplates();
    });
    return unsub;
  }, [navigation]);

  const loadCompanyProfile = async () => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (logo) setCompanyLogo(logo);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') ||
                    await AsyncStorage.getItem('token');
      const res = await fetch(
        `https://api.jobeng.ai/api/data/company_profiles?company_id=${cid}`,
        { headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      if (!res.ok) return;
      const r = await res.json();
      const cp = Array.isArray(r) ? r[0] : (r.data?.[0] ?? r.data ?? r);
      if (cp && typeof cp === 'object') {
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (logo) setCompanyLogo(logo);
        await AsyncStorage.setItem('company_profile', JSON.stringify(cp));
      }
    } catch {}
  };

  const onRefresh = async () => {
    setRefreshing(true);
    
    // Simulate data refresh
    setTimeout(() => {
      setRefreshing(false);
    }, 1000);
  };

  const navigateToScreen = (screenName, params = {}) => {
    if (navigation) {
      navigation.navigate(screenName, params);
    } else {
      Alert.alert('Navigation', `Would navigate to ${screenName}`);
    }
  };

  const useTemplate = (template) => {
    Alert.alert(
      'Use Template',
      `Would you like to use the "${template.title}" template to create a new job posting?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Customize', onPress: () => customizeTemplate(template) },
        { text: 'Use As Is', onPress: () => createJobFromTemplate(template) }
      ]
    );
  };

  const customizeTemplate = (template) => {
    // If this is a DB-backed custom template (has _dbId), open it in edit
    // mode so the user updates the original record. Otherwise duplicate
    // the built-in template into a new draft.
    if (template?._dbId) {
      navigateToScreen('CreateCustomTemplateScreen', {
        template: { ...template, id: template._dbId },
      });
    } else {
      navigateToScreen('CreateCustomTemplateScreen', {
        // Pass without _dbId / numeric id so the screen treats it as new
        initialTemplate: { ...template, id: null },
      });
    }
  };

  const createJobFromTemplate = async (template) => {
    // For DB-backed templates, log a usage row + bump usage_count so the
    // "uses" metric on the card actually reflects reality.
    if (template?._dbId) {
      try {
        const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10) || null;
        const uid = parseInt(await AsyncStorage.getItem('user_id')    || '0', 10) || null;
        await jtFetch('/api/data/job_template_usage', {
          method: 'POST',
          body: JSON.stringify({
            template_id: template._dbId,
            company_id:  cid,
            used_by:     uid,
          }),
        }).catch((e) => console.warn('[JobTemplates] usage log:', e.message));
        // Optimistic UI bump
        setCustomTemplates(prev => prev.map(t =>
          t._dbId === template._dbId ? { ...t, usage: (t.usage || 0) + 1 } : t,
        ));
        // Persist the new count so it survives refresh
        await jtFetch(`/api/data/job_templates/${template._dbId}`, {
          method: 'PUT',
          body: JSON.stringify({ usage_count: (template.usage || 0) + 1 }),
        }).catch(() => {});
      } catch (e) { console.warn('[JobTemplates] usage tracking:', e.message); }
    }
    navigateToScreen('CreateJobScreen', { template });
  };

  const previewTemplate = (template) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const saveCustomizedTemplate = () => {
    if (customizedTemplate) {
      createJobFromTemplate(customizedTemplate);
      setShowCustomizeModal(false);
    }
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#4A90E2', '#357ABD']}
      style={styles.header}
    >
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      
      <View style={[styles.headerContent, { position: 'relative' }]}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation?.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        
          {/* Company logo synced from CompanyProfileScreen */}
          <TouchableOpacity
            onPress={() => navigation?.navigate('CompanyProfileScreen')}
            style={{ position: 'absolute', left: 56, top: 0, bottom: 0, justifyContent: 'center' }}
          >
            {companyLogo
              ? <Image
                  source={{ uri: companyLogo || undefined }}
                  style={{ width: 34, height: 34, borderRadius: 17,
                    borderWidth: 2, borderColor: 'rgba(255,255,255,0.5)' }}
                  resizeMode="cover"
                />
              : <View style={{ width: 34, height: 34, borderRadius: 17,
                  backgroundColor: 'rgba(255,255,255,0.2)', borderWidth: 2,
                  borderColor: 'rgba(255,255,255,0.4)',
                  alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ color: '#fff', fontSize: 12, fontWeight: '900' }}>
                    {(companyName || 'CO').split(' ').slice(0, 2)
                      .map(w => w[0]?.toUpperCase() || '').join('') || 'CO'}
                  </Text>
                </View>}
          </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>Job Templates</Text>
          <Text style={[styles.headerSubtitle]}>
            {companyName || 'Pre-built templates for faster job posting'}
          </Text>
        </View>
        
        <TouchableOpacity 
          style={styles.searchButton}
          onPress={() => setShowSearchModal(true)}
        >
          <Ionicons name="search" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Template Stats */}
      <View style={styles.statsContainer}>
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>{getAllTemplates().length}</Text>
          <Text style={styles.statLabel}>Templates</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>{categories.length - 1}</Text>
          <Text style={styles.statLabel}>Categories</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>4.8</Text>
          <Text style={styles.statLabel}>Avg Rating</Text>
        </View>
      </View>
    </LinearGradient>
  );

  const renderCategories = () => (
    <View style={styles.categoriesContainer}>
      <Text style={styles.sectionTitle}>Categories</Text>
      
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={styles.categoriesScroll}
      >
        {categories.map((category) => (
          <TouchableOpacity
            key={category.id}
            style={[
              styles.categoryCard,
              selectedCategory === category.id && styles.activeCategoryCard
            ]}
            onPress={() => setSelectedCategory(category.id)}
          >
            <View style={[
              styles.categoryIcon,
              selectedCategory === category.id && styles.activeCategoryIcon
            ]}>
              <MaterialCommunityIcons 
                name={category.icon} 
                size={24} 
                color={selectedCategory === category.id ? '#fff' : '#4A90E2'} 
              />
            </View>
            <Text style={[
              styles.categoryName,
              selectedCategory === category.id && styles.activeCategoryName
            ]}>
              {category.name}
            </Text>
            <Text style={[
              styles.categoryCount,
              selectedCategory === category.id && styles.activeCategoryCount
            ]}>
              {category.count} templates
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderTemplateCard = (template) => (
    <View key={template.id} style={styles.templateCard}>
      <View style={styles.templateHeader}>
        <View style={styles.templateTitleContainer}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
            <Text style={styles.templateTitle}>{template.title}</Text>
            {template.isCustom ? (
              <View style={{
                backgroundColor: '#10B981', borderRadius: 8,
                paddingHorizontal: 6, paddingVertical: 1, marginLeft: 8,
              }}>
                <Text style={{ color: '#FFF', fontSize: 9, fontWeight: '700' }}>CUSTOM</Text>
              </View>
            ) : null}
          </View>
          <Text style={styles.templateIndustry}>{template.industry} • {template.level}</Text>
        </View>
        
        <View style={styles.templateBadges}>
          <View style={styles.popularityBadge}>
            <MaterialCommunityIcons name="fire" size={12} color="#ff6b35" />
            <Text style={styles.popularityText}>{template.popularity}%</Text>
          </View>
          
          <View style={styles.ratingBadge}>
            <MaterialCommunityIcons name="star" size={12} color="#ffd700" />
            <Text style={styles.ratingText}>{template.rating}</Text>
          </View>
        </View>
      </View>

      <Text style={styles.templateDescription} numberOfLines={2}>
        {template.description}
      </Text>

      <View style={styles.templateTags}>
        {template.tags.slice(0, 3).map((tag, index) => (
          <View key={index} style={styles.templateTag}>
            <Text style={styles.templateTagText}>{tag}</Text>
          </View>
        ))}
        {template.tags.length > 3 && (
          <View style={styles.templateTag}>
            <Text style={styles.templateTagText}>+{template.tags.length - 3}</Text>
          </View>
        )}
      </View>

      <View style={styles.templateStats}>
        <View style={styles.templateStat}>
          <MaterialCommunityIcons name="account-group" size={16} color="#64748B" />
          <Text style={styles.templateStatText}>{template.usage} uses</Text>
        </View>
        
        <View style={styles.templateStat}>
          <MaterialCommunityIcons name="clock-outline" size={16} color="#64748B" />
          <Text style={styles.templateStatText}>{template.type}</Text>
        </View>
      </View>

      <View style={styles.templateActions}>
        <TouchableOpacity 
          style={styles.previewButton}
          onPress={() => previewTemplate(template)}
        >
          <MaterialCommunityIcons name="eye-outline" size={18} color="#4A90E2" />
          <Text style={styles.previewButtonText}>Preview</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.useButton}
          onPress={() => useTemplate(template)}
        >
          <MaterialCommunityIcons name="plus" size={18} color="#fff" />
          <Text style={styles.useButtonText}>Use Template</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderTemplates = () => {
    const filteredTemplates = getFilteredTemplates();
    
    return (
      <View style={styles.templatesContainer}>
        <View style={styles.templatesHeader}>
          <Text style={styles.sectionTitle}>
            {selectedCategory === 'all' ? 'All Templates' : 
             categories.find(cat => cat.id === selectedCategory)?.name || 'Templates'}
          </Text>
          <Text style={styles.templatesCount}>
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
          </Text>
        </View>

        {filteredTemplates.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="file-document-outline" size={64} color="#CBD5E1" />
            <Text style={styles.emptyStateTitle}>No templates found</Text>
            <Text style={styles.emptyStateText}>
              <Text>{searchQuery ? 'Try adjusting your search terms' : 'No templates available in this category'}</Text>
            </Text>
          </View>
        ) : (
          <View style={styles.templatesList}>
            {filteredTemplates.map(template => renderTemplateCard(template))}
          </View>
        )}
      </View>
    );
  };

  const renderPreviewModal = () => (
    <Modal
      animationType="slide"
      transparent={false}
      visible={showPreviewModal}
      onRequestClose={() => setShowPreviewModal(false)}
    >
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.fullScreenContainer}>
        <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
        <SafeAreaView style={styles.safeContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity 
              style={styles.modalBackButton}
              onPress={() => setShowPreviewModal(false)}
            >
              <Ionicons name="arrow-back" size={24} color="#fff" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Template Preview</Text>
            <TouchableOpacity 
              style={styles.modalActionButton}
              onPress={() => {
                setShowPreviewModal(false);
                useTemplate(selectedTemplate);
              }}
            >
              <Text style={styles.modalActionText}>Use</Text>
            </TouchableOpacity>
          </View>
          
          {selectedTemplate && (
            <ScrollView style={styles.previewContent}>
              <View style={styles.previewCard}>
                {/* Job Title and Basic Info */}
                <View style={styles.previewSection}>
                  <Text style={styles.previewJobTitle}>{selectedTemplate.title}</Text>
                  <View style={styles.previewJobInfo}>
                    <Text style={styles.previewJobDetail}>
                      {selectedTemplate.customFields.location} • {selectedTemplate.type}
                    </Text>
                    <Text style={styles.previewJobDetail}>
                      £{selectedTemplate.customFields.salaryRange.min.toLocaleString()} - 
                      £{selectedTemplate.customFields.salaryRange.max.toLocaleString()}
                    </Text>
                  </View>
                </View>

                {/* Description */}
                <View style={styles.previewSection}>
                  <Text style={styles.previewSectionTitle}>Job Description</Text>
                  <Text style={styles.previewText}>{selectedTemplate.description}</Text>
                </View>

                {/* Requirements */}
                <View style={styles.previewSection}>
                  <Text style={styles.previewSectionTitle}>Requirements</Text>
                  {selectedTemplate.requirements.map((req, index) => (
                    <View key={index} style={styles.previewListItem}>
                      <Text style={styles.previewBullet}>•</Text>
                      <Text style={styles.previewListText}>{req}</Text>
                    </View>
                  ))}
                </View>

                {/* Responsibilities */}
                <View style={styles.previewSection}>
                  <Text style={styles.previewSectionTitle}>Responsibilities</Text>
                  {selectedTemplate.responsibilities.map((resp, index) => (
                    <View key={index} style={styles.previewListItem}>
                      <Text style={styles.previewBullet}>•</Text>
                      <Text style={styles.previewListText}>{resp}</Text>
                    </View>
                  ))}
                </View>

                {/* Benefits */}
                <View style={styles.previewSection}>
                  <Text style={styles.previewSectionTitle}>Benefits</Text>
                  {selectedTemplate.benefits.map((benefit, index) => (
                    <View key={index} style={styles.previewListItem}>
                      <Text style={styles.previewBullet}>•</Text>
                      <Text style={styles.previewListText}>{benefit}</Text>
                    </View>
                  ))}
                </View>

                {/* Skills */}
                <View style={styles.previewSection}>
                  <Text style={styles.previewSectionTitle}>Required Skills</Text>
                  <View style={styles.previewSkills}>
                    {selectedTemplate.customFields.skills.map((skill, index) => (
                      <View key={index} style={styles.previewSkillTag}>
                        <Text style={styles.previewSkillText}>{skill}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              </View>
            </ScrollView>
          )}
        </SafeAreaView>
      </LinearGradient>
    </Modal>
  );

  const renderCustomizeModal = () => (
    <Modal
      animationType="slide"
      transparent={false}
      visible={showCustomizeModal}
      onRequestClose={() => setShowCustomizeModal(false)}
    >
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.fullScreenContainer}>
        <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
        <SafeAreaView style={styles.safeContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity 
              style={styles.modalBackButton}
              onPress={() => setShowCustomizeModal(false)}
            >
              <Ionicons name="close" size={24} color="#fff" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Customize Template</Text>
            <TouchableOpacity 
              style={styles.modalActionButton}
              onPress={saveCustomizedTemplate}
            >
              <Text style={styles.modalActionText}>Save</Text>
            </TouchableOpacity>
          </View>
          
          {customizedTemplate && (
            <ScrollView style={styles.customizeContent}>
              <View style={styles.customizeCard}>
                {/* Basic Information */}
                <View style={styles.customizeSection}>
                  <Text style={styles.customizeSectionTitle}>Basic Information</Text>
                  
                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Job Title</Text>
                    <TextInput
                      style={styles.textInput}
                      value={customizedTemplate.title}
                      onChangeText={(text) => setCustomizedTemplate({
                        ...customizedTemplate,
                        title: text
                      })}
                      placeholder="Enter job title"
                    />
                  </View>

                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Location</Text>
                    <TextInput
                      style={styles.textInput}
                      value={customizedTemplate.customFields.location}
                      onChangeText={(text) => setCustomizedTemplate({
                        ...customizedTemplate,
                        customFields: {
                          ...customizedTemplate.customFields,
                          location: text
                        }
                      })}
                      placeholder="Enter location"
                    />
                  </View>

                  <View style={styles.inputRow}>
                    <View style={styles.inputHalf}>
                      <Text style={styles.inputLabel}>Min Salary (£)</Text>
                      <TextInput
                        style={styles.textInput}
                        value={customizedTemplate.customFields.salaryRange.min.toString()}
                        onChangeText={(text) => setCustomizedTemplate({
                          ...customizedTemplate,
                          customFields: {
                            ...customizedTemplate.customFields,
                            salaryRange: {
                              ...customizedTemplate.customFields.salaryRange,
                              min: parseInt(text) || 0
                            }
                          }
                        })}
                        placeholder="Min salary"
                        keyboardType="numeric"
                      />
                    </View>
                    
                    <View style={styles.inputHalf}>
                      <Text style={styles.inputLabel}>Max Salary (£)</Text>
                      <TextInput
                        style={styles.textInput}
                        value={customizedTemplate.customFields.salaryRange.max.toString()}
                        onChangeText={(text) => setCustomizedTemplate({
                          ...customizedTemplate,
                          customFields: {
                            ...customizedTemplate.customFields,
                            salaryRange: {
                              ...customizedTemplate.customFields.salaryRange,
                              max: parseInt(text) || 0
                            }
                          }
                        })}
                        placeholder="Max salary"
                        keyboardType="numeric"
                      />
                    </View>
                  </View>
                </View>

                {/* Job Description */}
                <View style={styles.customizeSection}>
                  <Text style={styles.customizeSectionTitle}>Job Description</Text>
                  <TextInput
                    style={[styles.textInput, styles.textArea]}
                    value={customizedTemplate.description}
                    onChangeText={(text) => setCustomizedTemplate({
                      ...customizedTemplate,
                      description: text
                    })}
                    placeholder="Enter job description"
                    multiline
                    numberOfLines={4}
                  />
                </View>

                {/* Remote Work Option */}
                <View style={styles.customizeSection}>
                  <Text style={styles.customizeSectionTitle}>Work Arrangement</Text>
                  <TouchableOpacity 
                    style={styles.checkboxContainer}
                    onPress={() => setCustomizedTemplate({
                      ...customizedTemplate,
                      customFields: {
                        ...customizedTemplate.customFields,
                        remote: !customizedTemplate.customFields.remote
                      }
                    })}
                  >
                    <View style={[
                      styles.checkbox,
                      customizedTemplate.customFields.remote && styles.checkboxChecked
                    ]}>
                      {customizedTemplate.customFields.remote && (
                        <MaterialCommunityIcons name="check" size={16} color="#fff" />
                      )}
                    </View>
                    <Text style={styles.checkboxLabel}>Remote work available</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>
          )}
        </SafeAreaView>
      </LinearGradient>
    </Modal>
  );

  const renderSearchModal = () => (
    <Modal
      animationType="slide"
      transparent={true}
      visible={showSearchModal}
      onRequestClose={() => setShowSearchModal(false)}
    >
      <View style={styles.searchModalOverlay}>
        <View style={styles.searchModalContainer}>
          <View style={styles.searchModalHeader}>
            <TextInput
              style={styles.searchInput}
              placeholder="Search templates..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoFocus={true}
            />
            <TouchableOpacity 
              style={styles.searchCloseButton}
              onPress={() => setShowSearchModal(false)}
            >
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          {searchQuery.trim() && (
            <ScrollView style={styles.searchResults}>
              {getFilteredTemplates().map(template => (
                <TouchableOpacity 
                  key={template.id}
                  style={styles.searchResultItem}
                  onPress={() => {
                    setShowSearchModal(false);
                    previewTemplate(template);
                  }}
                >
                  <Text style={styles.searchResultTitle}>{template.title}</Text>
                  <Text style={styles.searchResultSubtitle}>
                    {template.industry} • {template.level}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );

  if (isLoading) {
    return (
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.fullScreenContainer}>
        <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
        <SafeAreaView style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#FFFFFF" />
          <Text style={styles.loadingText}>Loading templates...</Text>
        </SafeAreaView>
      </LinearGradient>
    );
  }

  return (
    <LinearGradient colors={['#4A90E2', '#4A90E2']} style={styles.fullScreenContainer}>
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      <SafeAreaView style={styles.safeContainer}>
        <Animated.View style={[{ flex: 1 }, { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }]}>
          {renderHeader()}
          
          <View style={styles.contentContainer}>
            <ScrollView 
              style={styles.content}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
              }
              showsVerticalScrollIndicator={false}
            >
              {renderCategories()}
              {renderTemplates()}
            </ScrollView>
          </View>

          {/* Floating Action Button */}
          <TouchableOpacity 
            style={styles.fab}
            onPress={() => navigateToScreen('CreateCustomTemplateScreen')}
          >
            <MaterialCommunityIcons name="plus" size={24} color="#fff" />
          </TouchableOpacity>
        </Animated.View>

        {renderPreviewModal()}
        {renderCustomizeModal()}
        {renderSearchModal()}
      </SafeAreaView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  fullScreenContainer: {
    flex: 1,
  },
  safeContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginTop: 16,
  },
  header: {
    paddingTop: 10,
    paddingBottom: 15,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  searchButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 12,
    padding: 16,
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
  },
  statLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: 'hidden',
  },
  content: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  
  // Categories styles
  categoriesContainer: {
    padding: 20,
  },
  categoriesScroll: {
    marginHorizontal: -20,
    paddingHorizontal: 20,
  },
  categoryCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginRight: 12,
    width: 120,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  activeCategoryCard: {
    backgroundColor: '#4A90E2',
  },
  categoryIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#EEF2FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  activeCategoryIcon: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  categoryName: {
    fontSize: 12,
    fontWeight: '600',
    color: '#0F172A',
    textAlign: 'center',
    marginBottom: 4,
  },
  activeCategoryName: {
    color: '#fff',
  },
  categoryCount: {
    fontSize: 10,
    color: '#64748B',
    textAlign: 'center',
  },
  activeCategoryCount: {
    color: 'rgba(255, 255, 255, 0.8)',
  },

  // Templates styles
  templatesContainer: {
    padding: 20,
    paddingTop: 0,
  },
  templatesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  templatesCount: {
    fontSize: 14,
    color: '#64748B',
  },
  templatesList: {
    gap: 16,
  },
  templateCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  templateHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  templateTitleContainer: {
    flex: 1,
  },
  templateTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  templateIndustry: {
    fontSize: 14,
    color: '#64748B',
  },
  templateBadges: {
    alignItems: 'flex-end',
    gap: 4,
  },
  popularityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  popularityText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#D97706',
    marginLeft: 2,
  },
  ratingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF9C3',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  ratingText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#CA8A04',
    marginLeft: 2,
  },
  templateDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 12,
  },
  templateTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },
  templateTag: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  templateTagText: {
    fontSize: 11,
    color: '#475569',
    fontWeight: '500',
  },
  templateStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  templateStat: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  templateStatText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  templateActions: {
    flexDirection: 'row',
    gap: 12,
  },
  previewButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#4A90E2',
    borderRadius: 8,
    paddingVertical: 10,
  },
  previewButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4A90E2',
    marginLeft: 4,
  },
  useButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#4A90E2',
    borderRadius: 8,
    paddingVertical: 10,
  },
  useButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
    marginLeft: 4,
  },

  // Empty state styles
  emptyState: {
    alignItems: 'center',
    padding: 40,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#64748B',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#94A3B8',
    textAlign: 'center',
  },

  // Modal styles
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 20,
  },
  modalBackButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    textAlign: 'center',
  },
  modalActionButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
  },
  modalActionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },

  // Preview modal styles
  previewContent: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  previewCard: {
    margin: 20,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  previewSection: {
    marginBottom: 24,
  },
  previewJobTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 8,
  },
  previewJobInfo: {
    gap: 4,
  },
  previewJobDetail: {
    fontSize: 14,
    color: '#64748B',
  },
  previewSectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 8,
  },
  previewText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  previewListItem: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  previewBullet: {
    fontSize: 14,
    color: '#4A90E2',
    marginRight: 8,
    fontWeight: '600',
  },
  previewListText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  previewSkills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  previewSkillTag: {
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  previewSkillText: {
    fontSize: 12,
    color: '#4A90E2',
    fontWeight: '500',
  },

  // Customize modal styles
  customizeContent: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  customizeCard: {
    margin: 20,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  customizeSection: {
    marginBottom: 24,
  },
  customizeSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 6,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#0F172A',
    backgroundColor: '#fff',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  inputRow: {
    flexDirection: 'row',
    gap: 12,
  },
  inputHalf: {
    flex: 1,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    borderRadius: 4,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#4A90E2',
    borderColor: '#4A90E2',
  },
  checkboxLabel: {
    fontSize: 14,
    color: '#374151',
  },

  // Search modal styles
  searchModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-start',
    paddingTop: 60,
  },
  searchModalContainer: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.8,
    flex: 1,
  },
  searchModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#0F172A',
    paddingVertical: 8,
  },
  searchCloseButton: {
    padding: 8,
  },
  searchResults: {
    flex: 1,
  },
  searchResultItem: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  searchResultTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  searchResultSubtitle: {
    fontSize: 14,
    color: '#64748B',
  },

  // FAB styles
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#4A90E2',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});